import { useEffect, useState } from 'react';
import { cajaChicaService } from '../services/api';
import '../styles/CajaChica.css';

const CajaChica = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMovimiento, setEditingMovimiento] = useState(null);
  const [filtros, setFiltros] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    tipo: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    tipo_movimiento: 'entrada',
    concepto: '',
    monto: '',
    fecha: new Date().toISOString().split('T')[0],
    detalle: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    cargarMovimientos();
  }, [filtros]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      await Promise.all([
        cargarMovimientos(),
        cargarBalance()
      ]);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setError('Error cargando los datos');
    } finally {
      setLoading(false);
    }
  };

  const cargarMovimientos = async () => {
    try {
      const filtrosValidos = Object.fromEntries(
        Object.entries(filtros).filter(([, value]) => value !== '')
      );
      const response = await cajaChicaService.getMovimientos(filtrosValidos);
      setMovimientos(response.data);
    } catch (error) {
      console.error('Error cargando movimientos:', error);
      setError('Error cargando los movimientos');
    }
  };

  const cargarBalance = async () => {
    try {
      const response = await cajaChicaService.getBalance();
      setBalance(response.data);
    } catch (error) {
      console.error('Error cargando balance:', error);
      setError('Error cargando el balance');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      fecha_inicio: '',
      fecha_fin: '',
      tipo: ''
    });
  };

  const abrirModal = (movimiento = null) => {
    if (movimiento) {
      setEditingMovimiento(movimiento);
      setFormData({
        tipo_movimiento: movimiento.tipo,
        concepto: movimiento.concepto,
        monto: movimiento.monto.toString(),
        fecha: movimiento.fecha,
        detalle: movimiento.detalle || ''
      });
    } else {
      setEditingMovimiento(null);
      setFormData({
        tipo_movimiento: 'entrada',
        concepto: '',
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        detalle: ''
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEditingMovimiento(null);
    setError('');
    setSuccess('');
  };

  const validarFormulario = () => {
    if (!formData.concepto.trim()) {
      setError('El concepto es requerido');
      return false;
    }
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      setError('El monto debe ser mayor a 0');
      return false;
    }
    if (!formData.fecha) {
      setError('La fecha es requerida');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    try {
      setLoading(true);

      const movimientoData = {
        ...formData,
        monto: parseFloat(formData.monto)
      };

      await cajaChicaService.createMovimiento(movimientoData);
      setSuccess('Movimiento registrado exitosamente');

      // Recargar datos
      await cargarDatos();

      // Cerrar modal después de un momento
      setTimeout(() => {
        cerrarModal();
      }, 1500);

    } catch (error) {
      console.error('Error guardando movimiento:', error);
      setError(error.response?.data?.error || 'Error guardando el movimiento');
    } finally {
      setLoading(false);
    }
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(monto);
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO');
  };

  if (loading && movimientos.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading">Cargando datos de caja chica...</div>
      </div>
    );
  }

  return (
    <div className="caja-chica-container">
      {/* Header */}
      <div className="caja-chica-header">
        <h1>💰 Caja Chica</h1>
        <p>Gestión de movimientos de caja chica</p>
      </div>

      {/* Balance Cards */}
      {balance && (
        <div className="balance-section">
          <div className="balance-cards">
            <div className="balance-card balance-actual">
              <div className="card-icon">💳</div>
              <div className="card-content">
                <h3>Balance Actual</h3>
                <div className="card-value">{formatearMonto(balance.balance_actual)}</div>
              </div>
            </div>
            <div className="balance-card total-entradas">
              <div className="card-icon">⬆️</div>
              <div className="card-content">
                <h3>Total Entradas</h3>
                <div className="card-value">{formatearMonto(balance.total_entradas)}</div>
                <div className="card-count">{balance.count_entradas} movimientos</div>
              </div>
            </div>
            <div className="balance-card total-salidas">
              <div className="card-icon">⬇️</div>
              <div className="card-content">
                <h3>Total Salidas</h3>
                <div className="card-value">{formatearMonto(balance.total_salidas)}</div>
                <div className="card-count">{balance.count_salidas} movimientos</div>
              </div>
            </div>
            <div className="balance-card total-movimientos">
              <div className="card-icon">📋</div>
              <div className="card-content">
                <h3>Total Movimientos</h3>
                <div className="card-value">{balance.total_movimientos}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros y acciones */}
      <div className="filtros-section">
        <div className="filtros">
          <div className="filtro-grupo">
            <label>Fecha Inicio:</label>
            <input
              type="date"
              name="fecha_inicio"
              value={filtros.fecha_inicio}
              onChange={handleFiltroChange}
            />
          </div>
          <div className="filtro-grupo">
            <label>Fecha Fin:</label>
            <input
              type="date"
              name="fecha_fin"
              value={filtros.fecha_fin}
              onChange={handleFiltroChange}
            />
          </div>
          <div className="filtro-grupo">
            <label>Tipo:</label>
            <select
              name="tipo"
              value={filtros.tipo}
              onChange={handleFiltroChange}
            >
              <option value="">Todos</option>
              <option value="entrada">Entrada</option>
              <option value="salida">Salida</option>
            </select>
          </div>
          <button className="btn-limpiar" onClick={limpiarFiltros}>
            Limpiar
          </button>
        </div>
        <button className="btn-nuevo" onClick={() => abrirModal()}>
          ➕ Nuevo Movimiento
        </button>
      </div>

      {/* Tabla de movimientos */}
      <div className="movimientos-section">
        <div className="movimientos-header">
          <h2>📝 Historial de Movimientos</h2>
          <span className="movimientos-count">{movimientos.length} movimientos</span>
        </div>

        <div className="tabla-container">
          <table className="movimientos-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Concepto</th>
                <th>Monto</th>
                <th>Balance Anterior</th>
                <th>Balance Actual</th>
                <th>Responsable</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    No hay movimientos registrados
                  </td>
                </tr>
              ) : (
                movimientos.map((movimiento) => (
                  <tr key={movimiento.id}>
                    <td>{formatearFecha(movimiento.fecha)}</td>
                    <td>
                      <span className={`tipo-badge ${movimiento.tipo}`}>
                        {movimiento.tipo === 'entrada' ? '⬆️ Entrada' : '⬇️ Salida'}
                      </span>
                    </td>
                    <td>{movimiento.concepto}</td>
                    <td className={`monto ${movimiento.tipo}`}>
                      {movimiento.tipo === 'entrada' ? '+' : '-'}{formatearMonto(Math.abs(movimiento.monto))}
                    </td>
                    <td>{formatearMonto(movimiento.balance_anterior)}</td>
                    <td className="balance-actual-cell">
                      {formatearMonto(movimiento.balance_actual)}
                    </td>
                    <td>{movimiento.responsable}</td>
                    <td>{movimiento.detalle || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>➕ Nuevo Movimiento de Caja Chica</h2>
              <button className="btn-cerrar" onClick={cerrarModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {error && <div className="error-message">❌ {error}</div>}
              {success && <div className="success-message">✅ {success}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de Movimiento *</label>
                  <select
                    name="tipo_movimiento"
                    value={formData.tipo_movimiento}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="entrada">⬆️ Entrada</option>
                    <option value="salida">⬇️ Salida</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Concepto *</label>
                <input
                  type="text"
                  name="concepto"
                  value={formData.concepto}
                  onChange={handleInputChange}
                  placeholder="Ej: Compra de materiales de oficina"
                  required
                />
              </div>

              <div className="form-group">
                <label>Monto *</label>
                <input
                  type="number"
                  name="monto"
                  value={formData.monto}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label>Detalle</label>
                <textarea
                  name="detalle"
                  value={formData.detalle}
                  onChange={handleInputChange}
                  placeholder="Información adicional (opcional)"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancelar" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar" disabled={loading}>
                  {loading ? 'Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CajaChica;